import * as React from 'react';

import {
  ProfileOccupationChoices_DemandLevel as DemandLevel,
  ProfileOccupationChoices_FeatureTag as FeatureTag,
} from '__generated__/graphql-types';

import { useProfileOccupationChoices } from 'bundles/employability-common/queries/hooks/useProfileOccupationChoices';
import { convertProfileOccupationChoiceToPreferenceNaptimeType } from 'bundles/employability-common/queries/utils/convertProfileOccupationChoiceToPreferenceNaptimeType';
import useLearnerObjectCurrentOccupationData from 'bundles/onboarding-common/hooks/useLearnerObjectCurrentOccupationData';
import useLearnerObjectUpdateCurrentOccupation from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateCurrentOccupation';
import CurrentRoleEditor from 'bundles/personalization-settings/components/CurrentRoleEditor';
import SettingRow from 'bundles/personalization-settings/components/SettingRow';
import type { CurrentRoleValue } from 'bundles/personalization-settings/hooks/useCurrentRoleEditor';
import { useTrackPersonalizationSelectChange } from 'bundles/personalization-settings/hooks/usePersonalizationEventTracking';
import useSettingSave from 'bundles/personalization-settings/hooks/useSettingSave';

// Same occupation-tag filter onboarding-common's JobTitleQuestion uses, so
// the catalog offered here matches onboarding.
const OCCUPATION_CHOICE_FILTER = {
  occupationTags: [
    { demandLevel: DemandLevel.Low, featureTag: FeatureTag.BaselineV2 },
    { demandLevel: DemandLevel.High, featureTag: FeatureTag.BaselineV2 },
  ],
};

export type CurrentRoleSettingItemProps = {
  label: string;
};

const LABEL_ID = 'current-role-label';

const CurrentRoleSettingItem = ({ label }: CurrentRoleSettingItemProps) => {
  const { value: savedValue, loading: readLoading } = useLearnerObjectCurrentOccupationData({});
  const { updateCurrentOccupation } = useLearnerObjectUpdateCurrentOccupation();
  const trackSelectChange = useTrackPersonalizationSelectChange();

  const { occupations: rawOccupations, isProfileOccupationChoicesLoading: catalogLoading } =
    useProfileOccupationChoices({ occupationChoiceFilter: OCCUPATION_CHOICE_FILTER });

  const availableOccupations = React.useMemo(
    () => convertProfileOccupationChoiceToPreferenceNaptimeType(rawOccupations),
    [rawOccupations]
  );

  // Seed the editor once both the saved value and catalog are ready.
  // Name resolution for DEFINED occupations requires the catalog.
  const seededValue = React.useMemo<CurrentRoleValue>(() => {
    if (!savedValue) return null;
    // savedValue is Record<string,string>: { id } for DEFINED, { name } for USER_GENERATED
    const occupationId = savedValue.id;
    const occupationName = savedValue.name;
    if (occupationId) {
      const match = availableOccupations.find((o) => o.id === occupationId);
      return match ? { id: match.id, name: match.name } : null;
    }
    return occupationName ? { name: occupationName } : null;
  }, [savedValue, availableOccupations]);

  const {
    value: editorValue,
    notificationType,
    onChange: handleSave,
  } = useSettingSave(seededValue, (value) => {
    let payload: Record<string, string>;
    if (value && 'id' in value) {
      payload = { id: value.id };
    } else if (value) {
      payload = { otherText: value.name };
    } else {
      payload = {};
    }
    return updateCurrentOccupation(payload);
  });

  const handleChange = (value: CurrentRoleValue) => {
    handleSave(value);
    trackSelectChange('current_role', value ? [value.name] : []);
  };

  return (
    <SettingRow
      label={label}
      labelId={LABEL_ID}
      notificationType={notificationType}
      loading={readLoading || catalogLoading}
    >
      <CurrentRoleEditor
        labelId={LABEL_ID}
        value={editorValue}
        onChange={handleChange}
        availableOccupations={availableOccupations}
      />
    </SettingRow>
  );
};

export default CurrentRoleSettingItem;
